import { Component, Input, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faPen, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ExcelExportComponent } from '@progress/kendo-angular-excel-export';
import {
  AddEvent,
  CancelEvent,
  DataBindingDirective,
  EditEvent,
  GridComponent,
  PageChangeEvent,
  RemoveEvent,
  SaveEvent,
} from '@progress/kendo-angular-grid';
import { SelectEvent } from '@progress/kendo-angular-upload';
import { process } from '@progress/kendo-data-query';
import {
  plusIcon,
  searchIcon,
  SVGIcon,
  uploadIcon,
} from '@progress/kendo-svg-icons';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { FileRestrictionsDirective } from 'src/app/core/directives/file-restrictions.directive';
import { AgencyList } from 'src/app/modules/dashboard/interface/agency-list';
import { ReferenceService } from 'src/app/services/reference.service';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import {
  validateEmail,
  validateName,
} from 'src/app/shared/validators/validator';
import { categories, Category } from '../../../../modules/administration/constants/categories';
import { Product } from '../../../../modules/administration/constants/products';
import { RelatedServiceContractResponse } from '../../../../modules/administration/interfaces/admin-service-types';
import { ExportRelatedService } from '../../../../modules/administration/models/export-related-service-model';
import { SaveRelatedServiceContractModel } from '../../../../modules/administration/models/save-related-service-contract-model';
import { AdminService } from '../../../../modules/administration/services/admin.service';

const DEFAULT_PAGE_SIZE: number = 5;
const FA_ICON_PATH_INDEX: number = 4;
const FA_ICON_WIDTH_INDEX: number = 0;
const FA_ICON_HEIGHT_INDEX: number = 1;

@Component({
  selector: 'app-related-service-contract',
  templateUrl: './related-service-contract.component.html',
  styleUrls: ['./related-service-contract.component.scss'],
  standalone: false
})
export class RelatedServiceContractComponent {
  @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;
  @Input() locationDetails: AgencyList;
  public range: { start: Date | null; end: Date | null } = { start: null, end: null };
  public gridView: Product[];
  loadSpinner: boolean;
  agencyId: string;
  type: string;
  public checkIcon: SVGIcon = this.customFaIcon(faCheck);
  public xMarkIcons: SVGIcon = this.customFaIcon(faXmark);
  public penIcons: SVGIcon = this.customFaIcon(faPen);
  public searchIcon: SVGIcon = searchIcon;
  public plusIcon: SVGIcon = plusIcon;
  public uploadIcon: SVGIcon = uploadIcon;
  public relatedServiceSelection: string[];
  public gridData: any[];
  public categories: Category[] = categories;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public skip: number = 0;
  public formGroup: FormGroup;
  private editedRowIndex: number;
  inactiveRelatedServiceContract$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showRemoveandEditIcon: boolean;
  showRemoveAndEditIcon: boolean;
  relatedServiceExportData: ExportRelatedService[] = [];
  @ViewChild('excelexport') excelexport: ExcelExportComponent;
  createFormGroup = (dataItem: any): FormGroup =>
    new FormGroup({
      agencyRelatedServiceContractSk: new FormControl(
        dataItem.agencyRelatedServiceContractSk
      ),
      agencySk: new FormControl(dataItem.agencySk),
      contractSk: new FormControl(dataItem.contractSk),
      schoolType: new FormControl(dataItem.schoolType),
      disciplineId: new FormControl(dataItem.disciplineId),
      languageId: new FormControl(dataItem.languageId),
      borough: new FormControl(dataItem.borough),
      district: new FormControl(dataItem.district),
      awardedTierId: new FormControl(dataItem.awardedTierId),
      awardedPrice: new FormControl(dataItem.awardedPrice),
      orderOfUse: new FormControl(dataItem.orderOfUse),
      dateRange: new FormGroup({
        start: new FormControl(
          dataItem.effectiveStartDate
            ? new Date(dataItem.effectiveStartDate)
            : null
        ),
        end: new FormControl(
          dataItem.effectiveEndDate ? new Date(dataItem.effectiveEndDate) : null
        ),
      }),
      effectiveStartDate: new FormControl(dataItem.effectiveStartDate),
      effectiveEndDate: new FormControl(dataItem.effectiveEndDate),
      contactName: new FormControl(dataItem?.contactName, validateName(false)),
      contactTitle: new FormControl(dataItem?.contactTitle),
      contactEmail: new FormControl(
        dataItem?.contactEmail,
        validateEmail(false)
      ),
      contactPhone: new FormControl(dataItem?.contactPhone),
      agencyLocationSk: new FormControl(this.locationDetails.agencyLocationSk),
    });
  disciplineDropdown: IReferenceData[];
  schoolTypeDropdown: IReferenceData[];
  boroughDropdown: IReferenceData[];
  districtDropdown: IReferenceData[];
  awardedTierDropdown: IReferenceData[];
  languageDropdown: IReferenceData[];
  serviceContractDetails: RelatedServiceContractResponse;
  @ViewChild(FileRestrictionsDirective)
  appFileRestrictions!: FileRestrictionsDirective;
  startDate: Date;
  endDate: Date;
  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private referenceService: ReferenceService
  ) {
    this.route.params.subscribe((params: Params) => {
      this.agencyId = params.id;
    });
    this.getRelatedServiceList();
    this.getDropDownList();
  }
  getDropDownList() {
    this.loadSpinner = true;
    combineLatest([
      this.referenceService.getDropDownValues('Discipline'),
      this.referenceService.getDropDownValues('SchoolType'),
      this.referenceService.getDropDownValues('borough'),
      this.referenceService.getDropDownValues('district'),
      this.referenceService.getDropDownValues('AwardedTier'),
      this.referenceService.getDropDownValues('Language'),
    ]).subscribe(
      ([discipline, schoolType, borough, district, awardedTier, language]) => {
        this.loadSpinner = false;
        this.disciplineDropdown = discipline;
        this.schoolTypeDropdown = schoolType;
        this.boroughDropdown = borough;
        this.districtDropdown = district;
        this.awardedTierDropdown = awardedTier;
        this.languageDropdown = language;
      }
    );
  }
  onDateChange(): void {
    if (this.range.start && this.range.end) {

    this.startDate = this.range.start;
    this.endDate = this.range.end;

    this.getDropDownList();
    this.getRelatedServiceList();
    }
  }
  getRelatedServiceList(): void {
    this.loadSpinner = true;
    this.adminService
      .getAgencyRelatedServiceContract(this.agencyId,this.startDate,
      this.endDate)
      .subscribe((res: RelatedServiceContractResponse) => {
        this.loadSpinner = false;
        this.serviceContractDetails = res;
        this.gridData =
          this.serviceContractDetails?.agencyRelatedServiceContracts;
        const data: ExportRelatedService[] = this.gridData?.map(
          (item: any) =>
            new ExportRelatedService(item, {
              disciplineDropdown: this.disciplineDropdown,
              schoolTypeDropdown: this.schoolTypeDropdown,
              boroughDropdown: this.boroughDropdown,
              districtDropdown: this.districtDropdown,
              awardedTierDropdown: this.awardedTierDropdown,
              languageDropdown: this.languageDropdown,
            })
        );
        this.relatedServiceExportData = data;
      });
  }
  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[FA_ICON_PATH_INDEX]}" />`,
      viewBox: `0 0 ${icon.icon[FA_ICON_WIDTH_INDEX]} ${icon.icon[FA_ICON_HEIGHT_INDEX]}`,
    };
  }

  public onAgencyGridFilter(value: Event): void {
    const inputValue: Event = value;
    if (inputValue) {
      this.gridView = process(this.gridView, {
        filter: {
          logic: 'or',
          filters: [
            {
              field: 'schoolType',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'disciplineId',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'languageId',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'borough',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'district',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'awardedPrice',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'orderOfUse',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'lastName',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'contactName',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'contactEmail',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'contactTitle',
              operator: 'contains',
              value: inputValue,
            },
            {
              field: 'contactPhone',
              operator: 'contains',
              value: inputValue,
            },
          ],
        },
      }).data;

      this.dataBinding.skip = 0;
    } else {
      this.getRelatedServiceList();
    }
  }

  //table
  onGobalWaitListSelectionChange(_event: any): void {
    void _event;
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
  }

  public dropdownData(id: number, dropDownName: IReferenceData[]): IReferenceData | undefined {
    return dropDownName?.find((x: IReferenceData) => x.id === id);
  }

  public addHandler({ sender }: AddEvent): void {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup({
      agencySk: this.route.snapshot.paramMap.get('id'),
      schoolType: null,
      disciplineId: null,
      languageId: null,
      borough: null,
      district: null,
      awardedTierId: null,
      awardedPrice: null,
      orderOfUse: null,
      dateRange: null,
      agencyLocationSk: null,
      // effectiveStartDate: null,
      // effectiveEndDate: null,
    });
    sender.addRow(this.formGroup);
  }

  public editHandler({ sender, rowIndex, dataItem }: EditEvent): void {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(dataItem);

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }: CancelEvent): void {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew: _isNew }: SaveEvent): void {
    void _isNew;
    this.loadSpinner = true;
    const product = formGroup.value;
    const data = new SaveRelatedServiceContractModel({
      ...product,
      effectiveStartDate: product.dateRange.start,
      effectiveEndDate: product.dateRange.end,
    });
    this.adminService.addRelatedService(data).subscribe((data) => {
      if (data) {
        this.loadSpinner = false;
        this.getRelatedServiceList();
        this.relatedServiceSelection = [];
      }
    });

    sender.closeRow(rowIndex);
  }
  public closeInactiveRelatedServiceContractDialog() {
    this.inactiveRelatedServiceContract$.next(false);
    this.getRelatedServiceList();
    this.relatedServiceSelection = [];
  }
  showEditIcon(): void {
    this.showRemoveAndEditIcon = true;
  }
  public removeHandler({ dataItem: _dataItem }: RemoveEvent): void {
    void _dataItem;
  }
  public inactiveRelatedService() {
    this.inactiveRelatedServiceContract$.next(true);
  }
  private closeEditor(
    grid: GridComponent,
    rowIndex = this.editedRowIndex
  ): void {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  uploadFile(ev: SelectEvent): void {
    this.adminService.validateAndUploadFile(ev);
  }
}
